import React, { ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AdminSidebar } from './admin/AdminSidebar';

interface AdminLayoutProps {
  children?: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [cypressWaitComplete, setCypressWaitComplete] = React.useState(false);

  // Check if we're in Cypress test environment
  const isCypress = typeof window !== 'undefined' && (window as any).Cypress;

  // In Cypress, give Supabase a brief moment to restore session from localStorage
  React.useEffect(() => {
    if (isCypress && !user && !cypressWaitComplete) {
      const hasSession = typeof window !== 'undefined' &&
        Object.keys(window.localStorage).some(key =>
          key.startsWith('sb-') && key.includes('-auth-token')
        );

      if (hasSession) {
        // Wait 1500ms for Supabase to restore session, then allow redirect if still no user
        const timer = setTimeout(() => {
          setCypressWaitComplete(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isCypress, user, cypressWaitComplete]);

  React.useEffect(() => {
    async function checkAdminStatus() {
      // In Cypress, wait briefly if session exists in localStorage but user not yet set
      if (isCypress && !user && !cypressWaitComplete) {
        const hasSession = typeof window !== 'undefined' &&
          Object.keys(window.localStorage).some(key =>
            key.startsWith('sb-') && key.includes('-auth-token')
          );
        if (hasSession) {
          return; // Wait for session to be restored
        }
      }

      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role !== 'admin') {
        navigate('/');
        return;
      }

      setIsAdmin(true);
    }

    checkAdminStatus();
  }, [user, navigate, isCypress, cypressWaitComplete]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <AdminSidebar />
      <div className="flex-1 p-8">
        {children || <Outlet />}
      </div>
    </div>
  );
} 